/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export interface ObjectiveCriteria {
  type: 'threshold' | 'count' | 'combination' | 'sequence' | 'timed';
  value: number | string;
  operator?: '>' | '<' | '>=' | '<=' | '==' | 'contains';
  timeframe?: number; // in game ticks
  conditions?: ObjectiveCondition[];
}

export interface ObjectiveCondition {
  stat: string;
  operator: '>' | '<' | '>=' | '<=' | '==';
  value: number;
  description?: string;
}

export interface ObjectivePrerequisite {
  type: 'objective' | 'building' | 'research' | 'product' | 'stat';
  id: string;
  description: string;
  completed?: boolean;
}

export interface ObjectiveReward {
  type: 'money' | 'research_points' | 'building_unlock' | 'product_unlock' | 'stat_boost' | 'special';
  value: number | string;
  description: string;
}

export interface ObjectiveCategory {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  order: number;
  subcategories?: ObjectiveSubcategory[];
}

export interface ObjectiveSubcategory {
  id: string;
  name: string;
  description: string;
  parentId: string;
  order: number;
}

export interface EnhancedObjective {
  id: string;
  title: string;
  description: string;
  detailedDescription: string;
  categoryId: string;
  subcategoryId?: string;
  difficulty: 'trivial' | 'easy' | 'medium' | 'hard' | 'expert';
  importance: 'low' | 'medium' | 'high' | 'critical';
  status: 'locked' | 'available' | 'in_progress' | 'completed' | 'failed';
  progress: number;
  maxValue: number;
  criteria: ObjectiveCriteria;
  prerequisites: ObjectivePrerequisite[];
  rewards: ObjectiveReward[];
  timeLimit?: number; // in game ticks
  timeStarted?: number;
  timeCompleted?: number;
  isRepeatable: boolean;
  completionBonus?: {
    earlyCompletion: number; // bonus % for completing early
    perfectExecution: number; // bonus % for completing with no penalties
  };
  hints: string[];
  successMetrics: {
    primary: string;
    secondary?: string[];
    tertiary?: string[];
  };
  failureConditions?: ObjectiveCondition[];
  dependencies: string[]; // other objective IDs that must be completed first
  unlocks: string[]; // objective IDs that this unlocks
  tags: string[];
  isHidden: boolean; // hidden until prerequisites are met
  isMandatory: boolean; // must be completed to progress
}

export interface ObjectiveProgress {
  objectiveId: string;
  currentProgress: number;
  lastUpdated: number;
  milestones: {
    reached: number;
    total: number;
    values: number[];
  };
  events: {
    timestamp: number;
    type: 'started' | 'milestone' | 'completed' | 'failed';
    value: number;
    message: string;
  }[];
}

export interface ObjectiveSet {
  id: string;
  name: string;
  description: string;
  categoryIds: string[];
  objectives: string[]; // objective IDs
  requiredCompletions: number;
  rewards: ObjectiveReward[];
  isSequential: boolean; // must complete in order
  timeLimit?: number;
}

export interface ObjectivesState {
  categories: ObjectiveCategory[];
  objectives: Record<string, EnhancedObjective>;
  progress: Record<string, ObjectiveProgress>;
  activeSets: Record<string, ObjectiveSet>;
  completedSets: string[];
  currentFocus: string | null; // currently tracked objective ID
  autoTrack: boolean;
  showHints: boolean;
  filterDifficulty: boolean;
  filterCategory: string | null;
  sortBy: 'importance' | 'difficulty' | 'progress' | 'deadline' | 'category';
  viewMode: 'list' | 'grid' | 'tree' | 'kanban';
}

export interface ObjectiveNotification {
  type: 'unlocked' | 'progress' | 'milestone' | 'completed' | 'failed' | 'warning' | 'bonus';
  objectiveId: string;
  title: string;
  message: string;
  timestamp: number;
  severity?: 'info' | 'success' | 'warning' | 'error';
  actions?: {
    label: string;
    action: string;
  }[];
}

// Objective evaluation context
export interface ObjectiveContext {
  currentStats: {
    budget: number;
    compliance: number;
    threat: number;
    day: number;
  };
  buildings: Record<string, number>;
  products: string[];
  research: string[];
  gameSpeed: number;
  scenario: string | null;
}

// Predefined objective categories
export const OBJECTIVE_CATEGORIES: ObjectiveCategory[] = [
  {
    id: 'infrastructure',
    name: 'Infrastructure',
    description: 'Build and upgrade core systems and facilities',
    icon: '🏗️',
    color: '#3b82f6',
    order: 1,
    subcategories: [
      {
        id: 'core_systems',
        name: 'Core Systems',
        description: 'Essential banking infrastructure',
        parentId: 'infrastructure',
        order: 1
      },
      {
        id: 'security_infrastructure',
        name: 'Security Infrastructure',
        description: 'Security and monitoring systems',
        parentId: 'infrastructure',
        order: 2
      },
      {
        id: 'compliance_infrastructure',
        name: 'Compliance Infrastructure',
        description: 'Regulatory and compliance systems',
        parentId: 'infrastructure',
        order: 3
      }
    ]
  },
  {
    id: 'compliance',
    name: 'Compliance & Governance',
    description: 'Achieve regulatory compliance and governance standards',
    icon: '⚖️',
    color: '#8b5cf6',
    order: 2,
    subcategories: [
      {
        id: 'basic_compliance',
        name: 'Basic Compliance',
        description: 'Essential regulatory requirements',
        parentId: 'compliance',
        order: 1
      },
      {
        id: 'advanced_compliance',
        name: 'Advanced Compliance',
        description: 'Complex regulatory frameworks',
        parentId: 'compliance',
        order: 2
      },
      {
        id: 'certifications',
        name: 'Certifications',
        description: 'Industry certifications and audits',
        parentId: 'compliance',
        order: 3
      }
    ]
  },
  {
    id: 'products',
    name: 'Products & Services',
    description: 'Launch and manage financial products and services',
    icon: '💼',
    color: '#10b981',
    order: 3,
    subcategories: [
      {
        id: 'consumer_products',
        name: 'Consumer Products',
        description: 'Retail banking products',
        parentId: 'products',
        order: 1
      },
      {
        id: 'business_products',
        name: 'Business Products',
        description: 'Corporate and business services',
        parentId: 'products',
        order: 2
      },
      {
        id: 'specialized_products',
        name: 'Specialized Products',
        description: 'Niche and specialized financial services',
        parentId: 'products',
        order: 3
      }
    ]
  },
  {
    id: 'security',
    name: 'Security Operations',
    description: 'Implement and maintain security measures',
    icon: '🔒',
    color: '#ef4444',
    order: 4,
    subcategories: [
      {
        id: 'preventive_security',
        name: 'Preventive Security',
        description: 'Proactive security measures',
        parentId: 'security',
        order: 1
      },
      {
        id: 'detective_security',
        name: 'Detective Security',
        description: 'Monitoring and detection systems',
        parentId: 'security',
        order: 2
      },
      {
        id: 'responsive_security',
        name: 'Responsive Security',
        description: 'Incident response and recovery',
        parentId: 'security',
        order: 3
      }
    ]
  },
  {
    id: 'business',
    name: 'Business Growth',
    description: 'Achieve business and financial milestones',
    icon: '📈',
    color: '#f59e0b',
    order: 5,
    subcategories: [
      {
        id: 'financial_metrics',
        name: 'Financial Metrics',
        description: 'Revenue and budget targets',
        parentId: 'business',
        order: 1
      },
      {
        id: 'customer_growth',
        name: 'Customer Growth',
        description: 'Customer acquisition and retention',
        parentId: 'business',
        order: 2
      },
      {
        id: 'market_expansion',
        name: 'Market Expansion',
        description: 'Geographic and market expansion',
        parentId: 'business',
        order: 3
      }
    ]
  },
  {
    id: 'research_innovation',
    name: 'Research & Innovation',
    description: 'Research technologies and drive innovation',
    icon: '🔬',
    color: '#06b6d4',
    order: 6,
    subcategories: [
      {
        id: 'basic_research',
        name: 'Basic Research',
        description: 'Fundamental technologies',
        parentId: 'research_innovation',
        order: 1
      },
      {
        id: 'advanced_research',
        name: 'Advanced Research',
        description: 'Cutting-edge technologies',
        parentId: 'research_innovation',
        order: 2
      },
      {
        id: 'innovation_projects',
        name: 'Innovation Projects',
        description: 'Special research initiatives',
        parentId: 'research_innovation',
        order: 3
      }
    ]
  }
];